import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import Leaderboard from './Leaderboard';

const HEXTRIS_URL = '/hextris/index.html';

export default function ArcadeZone({ onRequireAuth }) {
  const { user, token } = useAuth();
  const iframeRef = useRef(null);
  const [status, setStatus] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const submitting = useRef(false);

  const submitScore = useCallback(async (score) => {
    if (submitting.current) return;
    if (!user || !token) {
      setStatus('Login to save your score.');
      onRequireAuth?.();
      return;
    }
    if (!Number.isFinite(score) || score < 0) return;
    submitting.current = true;
    setStatus('Saving score...');
    try {
      await api('/api/scores', {
        method: 'POST',
        body: { game: 'hextris', score: Math.floor(score) },
        token,
      });
      setStatus(`Score saved: ${Math.floor(score)}`);
      setRefreshTick(t => t + 1);
    } catch (e) {
      setStatus(e.message || 'Failed to save score');
    } finally {
      submitting.current = false;
      setTimeout(() => setStatus(''), 3500);
    }
  }, [user, token, onRequireAuth]);

  useEffect(() => {
    const handler = (event) => {
      if (!event?.data) return;
      const data = event.data;
      let type, score;
      if (typeof data === 'object') {
        type = data.type || data.event;
        score = data.score ?? data.value ?? data.payload?.score;
      } else if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          type = parsed.type || parsed.event;
          score = parsed.score ?? parsed.value;
        } catch { return; }
      } else {
        return;
      }
      const t = String(type || '').toLowerCase();
      const isGameOver = t.includes('gameover') || t === 'game_over';
      if (!isGameOver) return;

      // Reject messages NOT coming from our iframe (defense in depth)
      const fromOurIframe = !iframeRef.current || event.source === iframeRef.current.contentWindow;
      // eslint-disable-next-line no-console
      console.log('[Arcade] game over message received, score =', score, 'fromOurIframe =', fromOurIframe);
      if (!fromOurIframe) return;

      const n = Number(score);
      if (!Number.isFinite(n) || n < 0) {
        setStatus('Invalid score from game.');
        setTimeout(() => setStatus(''), 2500);
        return;
      }
      if (n === 0) {
        setStatus('Score 0 — not saved.');
        setTimeout(() => setStatus(''), 2500);
        return;
      }
      submitScore(n);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [submitScore]);

  return (
    <section className="arcade">
      <div className="arcade-head">
        <h2>Arcade Zone</h2>
        <div className="arcade-status">{status}</div>
      </div>
      <div className="arcade-grid">
        <div className="arcade-game glass">
          <div className="arcade-game-bar">
            <span className="game-title">Hextris</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" onClick={() => setReloadKey(k => k + 1)}>Restart</button>
            </div>
          </div>
          <div className="iframe-wrap">
            <iframe
              key={reloadKey}
              ref={iframeRef}
              src={HEXTRIS_URL}
              title="Hextris"
              sandbox="allow-scripts allow-same-origin"
              loading="lazy"
            />
          </div>
          {!user && (
            <div className="game-hint">
              <button onClick={onRequireAuth}>Login</button> to save your scores.
            </div>
          )}
        </div>
        <Leaderboard refreshTick={refreshTick} />
      </div>
    </section>
  );
}
